import React, { useState, useEffect, useRef } from 'react';
import { useStoryId } from '../../hooks/useStoryId';
import { bookmarksApi } from '../../api/bookmarks';
import BookmarkItem from './BookmarkItem';
import Button from '../Common/Button';
import Loading from '../Common/Loading';
import ErrorMessage from '../Common/ErrorMessage';
import './Bookmarks.css';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../Common/useConfirm';

const CATEGORIES = [
  { value: null, label: 'All' },
  { value: 'character', label: 'Characters', color: '#3B82F6' },
  { value: 'plot', label: 'Plot', color: '#10B981' },
  { value: 'setting', label: 'Setting', color: '#8B5CF6' },
  { value: 'theme', label: 'Themes', color: '#F59E0B' }
];

function BookmarkList({ onCreateSynthesis }) {
  const { storyId, isLoading: storyLoading, error: storyError } = useStoryId();
  const [bookmarks, setBookmarks] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { confirm, modalProps } = useConfirm();

  useEffect(() => {
    loadBookmarks();
  }, [storyId, activeCategory]);

  const loadBookmarks = async () => {
    if (!storyId) {
      setBookmarks([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await bookmarksApi.list(storyId, activeCategory);
      setBookmarks(data);
    } catch (err) {
      setError(err.message || 'Failed to load bookmarks');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRef = useRef(loadBookmarks);
  loadRef.current = loadBookmarks;
  useEffect(() => {
    const refresh = () => loadRef.current();
    window.addEventListener('bookmark-created', refresh);
    window.addEventListener('bookmark-removed', refresh);
    return () => {
      window.removeEventListener('bookmark-created', refresh);
      window.removeEventListener('bookmark-removed', refresh);
    };
  }, []);

  const handleDelete = async (id) => {
    const ok = await confirm({ title: 'Delete bookmark?', message: 'Are you sure you want to delete this bookmark?', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;

    try {
      await bookmarksApi.delete(id);
      setBookmarks(bookmarks.filter(b => b.id !== id));
      window.dispatchEvent(new CustomEvent('bookmark-removed'));
    } catch (err) {
      alert('Failed to delete bookmark: ' + err.message);
    }
  };

  const handleCategoryChange = async (id, newCategory) => {
    try {
      await bookmarksApi.updateCategory(id, newCategory);
      // Reload to ensure consistency with current filter
      loadBookmarks();
    } catch (err) {
      alert('Failed to update category: ' + err.message);
    }
  };

  if (storyLoading || isLoading) {
    return <Loading size="large" text="Loading bookmarks..." />;
  }

  if (storyError) {
    return <ErrorMessage message={storyError} />;
  }

  if (!storyId) {
    return (
      <div className="bookmarks-empty">
        <p>No story found. Start chatting to create your first story!</p>
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadBookmarks} />;
  }

  return (
    <div className="bookmarks-container">
      <div className="bookmarks-header">
        <div className="category-filters">
          {CATEGORIES.map(cat => (
            <button
              key={cat.value || 'all'}
              className={`category-filter ${activeCategory === cat.value ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.value)}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <Button variant="primary" onClick={onCreateSynthesis}>
          Create Synthesis
        </Button>
      </div>

      {bookmarks.length === 0 ? (
        <div className="bookmarks-empty">
          <p>No bookmarks yet</p>
          <p className="bookmarks-empty-hint">
            Bookmark messages from chat sessions or create synthesis notes
          </p>
        </div>
      ) : (
        <div className="bookmarks-list">
          {bookmarks.map(bookmark => (
            <BookmarkItem
              key={bookmark.id}
              bookmark={bookmark}
              onDelete={handleDelete}
              onCategoryChange={handleCategoryChange}
            />
          ))}
        </div>
      )}
      <ConfirmModal {...modalProps} />
    </div>
  );
}

export default BookmarkList;
